import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
// New Images
import hero1 from '../assets/hero_bg_1.jpeg';
import hero2 from '../assets/hero_bg_2.jpeg';
import hero3 from '../assets/hero_bg_3.jpeg';
// Old Images
import old1 from '../assets/img11.png';
import old2 from '../assets/img4.png';


const HeroSection = () => {
    const [activeSlide, setActiveSlide] = useState(0);
    const { language } = useLanguage();

    // Combined Images array
    const images = [
        hero1,
        hero2,
        hero3,
        old1,
        old2,
    ];

    const totalSlides = images.length;

    // Memoize nextSlide to keep it stable across renders unless dependencies change
    const nextSlide = useCallback(() => {
        setActiveSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
    }, [totalSlides]);

    // Auto slide effect
    useEffect(() => {
        const interval = setInterval(() => {
            nextSlide();
        }, 5000);
        return () => clearInterval(interval);
    }, [nextSlide]); // Now safe to include nextSlide as a dependency

    return (
        <div className="relative h-screen bg-black">
            <div className="absolute inset-0 overflow-hidden">
                {images.map((img, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: activeSlide === index ? 1 : 0 }}
                        transition={{ duration: 1 }}
                        className="absolute inset-0"
                    >
                        <div className="absolute inset-0 bg-black/60 z-10" /> {/* Darker overlay */}
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/40 to-transparent z-10" />

                        <motion.img
                            src={img}
                            alt={`Slide ${index + 1}`}
                            initial={{ scale: 1.1 }}
                            animate={{ scale: activeSlide === index ? 1 : 1.1 }}
                            transition={{ duration: 6, ease: "linear" }}
                            className="object-cover w-full h-full"
                        />
                    </motion.div>
                ))}
            </div>

            {/* Slide indicators */}
            <div className="absolute bottom-8 left-0 right-0 flex justify-center space-x-2 z-20">
                {Array.from({ length: totalSlides }).map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setActiveSlide(index)}
                        className={`h-1.5 rounded-full transition-all duration-300 ${activeSlide === index
                                ? 'bg-blue-500 w-8'
                                : 'bg-white/30 w-4 hover:bg-white/60'
                            }`}
                        aria-label={`Go to slide ${index + 1}`}
                    />
                ))}
            </div>

            <div className="absolute inset-0 flex items-center z-20">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <h1 className="text-6xl md:text-8xl font-heading font-bold mb-4 leading-tight text-white">
                                HASSAN <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">LAFANI</span>
                                <br />
                                <span className="text-4xl md:text-6xl text-gray-200">
                                    {language === 'en' ? 'BOXING & FITNESS' : 'BOXE & FITNESS'}
                                </span>
                            </h1>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="mb-10"
                        >
                            <p className="text-xl md:text-2xl text-gray-300 font-light leading-relaxed max-w-xl">
                                {language === 'en'
                                    ? "Unleash your strength and confidence with expert-led boxing, kickboxing, and fitness training."
                                    : "Libérez votre force et votre confiance avec un entraînement expert en boxe, kickboxing et fitness."}
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                        >
                            <a
                                href="https://www.instagram.com/hassan_lafani?igsh=MTJsMTJjbzdjdTRodg%3D%3D&utm_source=qr"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white transition-all duration-200 bg-blue-600 font-heading rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
                            >
                                {language === 'en' ? 'START TRAINING' : 'COMMENCER'}
                                <svg className="w-5 h-5 ml-2 -mr-1 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </a>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HeroSection;
